import { CSSProperties } from 'react'

import { ArrowUpwardOutlined } from '@mui/icons-material'
import {
  styled,
  TableCell,
  TableRow,
  CSSObject,
  Theme,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  Tooltip,
  IconButton,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { getRowDividerColor, getRowSeparatorColor } from '@shared/components/Table'

const sharedStyles = ({ theme, height }: { theme: Theme; height?: number }): CSSObject => ({
  height: height ?? 'auto',
  position: 'relative',
  padding: theme.spacing(0, 0.5),
  display: 'flex',
  alignItems: 'center',
  borderBottom: 'none',
})

const checkboxStyles = ({
  theme,
  isSelected,
  isHeader,
}: {
  theme: Theme
  isSelected: boolean
  isHeader?: boolean
}): CSSObject => ({
  justifyContent: 'center',

  '&:before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: 4,
    backgroundColor: isSelected ? theme.palette.primary.main : 'inherit',
  },

  '&:after': {
    content: '""',
    right: 0,
    top: 0,
    position: 'absolute',
    height: '100%',
    width: 4,
    backgroundColor: getRowDividerColor({ theme, isSelected: isSelected && !isHeader }),
  },
})

export const Wrapper = styled(TableContainer)(({ theme }) => ({
  overflow: 'auto',
  height: '100%',
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
}))

export const Root = styled(Table)({
  display: 'grid',
  borderCollapse: 'collapse',
  minWidth: '100%',
})

export const Head = styled(TableHead)(({ theme }) => ({
  display: 'grid',
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.fab,
}))

type BodyProps = {
  height?: number
}

export const Body = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'height',
})<BodyProps>(({ height }) => ({
  display: 'grid',
  height: height ?? 'auto',
}))

type HeadTableRowProps = {
  height?: number
  columnWidth: { gridTemplateColumns: string }
}

export const HeadTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'height' && prop !== 'columnWidth',
})<HeadTableRowProps>(({ theme, height, columnWidth }) => ({
  boxSizing: 'content-box',

  position: 'relative',
  display: 'grid',
  alignItems: 'center',
  borderBottom: '4px solid grey',
  borderBottomColor: theme.palette.background.levels[100],
  backgroundColor: theme.palette.background.levels[500],

  height: height ?? 'auto',

  ...columnWidth,
}))

export type RowProps = {
  height?: number
  columnWidth: { gridTemplateColumns: string }
  isIndex: boolean
}

export const BodyTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'height' && prop !== 'columnWidth' && prop !== 'isIndex',
})<RowProps>(({ theme, height, columnWidth, isIndex }) => ({
  boxSizing: 'content-box',
  display: 'grid',
  height: height ?? 'auto',
  backgroundColor: theme.palette.background.levels[300],

  borderBottomWidth: isIndex ? 4 : 'inherit',
  borderBottomStyle: isIndex ? 'solid' : 'inherit',
  borderBottomColor: isIndex ? getRowSeparatorColor({ theme }) : 'inherit',

  ...columnWidth,
}))

export const SubRow = styled(TableRow)({
  boxSizing: 'content-box',
  display: 'grid',
  backgroundColor: 'transparent',
  overflow: 'hidden',
})

type CheckboxTableCellProps = {
  height?: number
  isSelected: boolean
  isHeader?: boolean
}

export const CheckboxTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'height' && prop !== 'isSelected' && prop !== 'isHeader',
})<CheckboxTableCellProps>(({ theme, height, isSelected, isHeader }) => ({
  ...sharedStyles({ theme, height }),
  ...checkboxStyles({ theme, isSelected, isHeader }),
}))

type DynamicContentTableCellProps = {
  height?: number
  horizontalAlignment?: CSSProperties['justifyContent']
  horizontalPadding?: number
  isLinkCell?: boolean
  autoSizeRows?: boolean
}

export const DynamicContentTableCell = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== 'height' &&
    prop !== 'horizontalAlignment' &&
    prop !== 'horizontalPadding' &&
    prop !== 'isLinkCell' &&
    prop !== 'autoSizeRows',
})<DynamicContentTableCellProps>(
  ({ theme, height, horizontalAlignment, horizontalPadding, isLinkCell, autoSizeRows }) => ({
    ...sharedStyles({ theme, height }),

    whiteSpace: autoSizeRows ? 'normal' : 'none',
    overflow: autoSizeRows ? 'visible' : 'hidden',
    wordBreak: autoSizeRows ? 'break-word' : 'normal',
    fontSize: theme.typography.text.fontSize,
    justifyContent: horizontalAlignment ?? 'flex-start',
    padding: theme.spacing(0, horizontalPadding ?? 0),
    cursor: isLinkCell ? 'pointer' : 'default',
  }),
)

type ActionMenuTableCellProps = {
  height?: number
}

export const ActionMenuTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'height',
})<ActionMenuTableCellProps>(({ theme, height }) => ({
  ...sharedStyles({ theme, height }),

  padding: theme.spacing(0, 2),
}))

type ScrollToTopButtonProps = {
  onClick: () => void
  id?: string
  tooltipTitle?: string
}

export const ScrollToTopButton = ({ onClick, id, tooltipTitle }: ScrollToTopButtonProps) => {
  const { t } = useTranslation(['common'])

  return (
    <Tooltip title={tooltipTitle || t('scrollToTop')} placement="left" arrow>
      <IconButton
        id={id}
        onClick={(e) => {
          e.stopPropagation()
          onClick()
        }}
        sx={{
          position: 'absolute',
          right: 70,
          bottom: 70,
          zIndex: (theme) => theme.zIndex.tooltip,
          backgroundColor: (theme) => theme.palette.background.contrastLevels[100],
        }}
      >
        <ArrowUpwardOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}
